import { useQuery } from "@tanstack/react-query";
import { Button, Card, DatePicker, Form, Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import type {
  AssignationRowBase,
  CongregationArchiveRow,
  ListResponse,
  SpeakerArchiveRow,
  ZoneArchiveRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

const API_DATE = "YYYY-MM-DD";

type Scope = "circumscription" | "congregation" | "speaker";

/**
 * An index signature rather than two optional fields: withQuery takes a plain
 * record, and TypeScript does not treat a closed interface as one.
 */
type Period = Record<string, string | undefined>;

const PATHS: Record<Scope, string> = {
  circumscription: "/circumscription/archive",
  congregation: "/congregation/archive",
  speaker: "/speaker/archive",
};

/**
 * Three sidebar entries, one screen:
 *
 * - "circumscription_archive" → /circumscription/circumscription_archive
 * - "congregation_archive"    → /congregation/congregation_archive
 * - "speaker_archive"         → /speaker/speaker_archive
 *
 * Past events, read-only. Each role looks at its own calendar, which is why the
 * three answers differ in what they carry: the zone has little more than the
 * event itself, the congregation adds who spoke, the speaker adds where.
 *
 * The period is the server's to decide. It clamps `date_from` to
 * `hide_events_after_months` back — the setting each role has on its own
 * settings screen — and `date_to` to the current week, so a date outside the
 * window silently comes back as the nearest one inside it.
 */
export function ArchivePage({ scope }: { scope: Scope }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>({});

  const rows = useQuery({
    queryKey: ["archive", scope, period],
    queryFn: () =>
      apiFetch<ListResponse<AssignationRowBase>>(withQuery(PATHS[scope], period)),
  });

  const title =
    scope === "circumscription"
      ? t("navigation.circumscription_archive")
      : scope === "congregation"
        ? t("navigation.congregation_archive")
        : t("navigation.speaker_archive");

  return (
    <Card title={title}>
      <PeriodFilter onApply={setPeriod} loading={rows.isFetching} />

      <ArchiveTable
        scope={scope}
        rows={rows.data?.data ?? []}
        loading={rows.isPending}
        error={rows.error}
        exportAs={title}
      />
    </Card>
  );
}

/** Two dates rather than the text FilterBar — a period is picked, not typed. */
function PeriodFilter({
  onApply,
  loading,
}: {
  onApply: (period: Period) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ date_from?: dayjs.Dayjs; date_to?: dayjs.Dayjs }>();

  return (
    <Form
      form={form}
      layout="inline"
      style={{ marginBottom: 16, rowGap: 8 }}
      onFinish={(values) =>
        onApply({
          date_from: values.date_from?.format(API_DATE),
          date_to: values.date_to?.format(API_DATE),
        })
      }
    >
      <Form.Item name="date_from" label={t("filter.from")}>
        <DatePicker />
      </Form.Item>
      <Form.Item name="date_to" label={t("filter.to")}>
        <DatePicker />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("filter.search")}
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              onApply({});
            }}
          >
            {t("app.filter_reset")}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

/**
 * The three calendars, read-only. Exported because the landing screen shows the
 * same rows from the two preview endpoints — same serializers, same columns —
 * and only the hint below the table is specific to the archive.
 */
export function ArchiveTable({
  scope,
  rows,
  loading,
  error,
  hint = true,
  exportAs,
}: {
  scope: Scope;
  rows: AssignationRowBase[];
  loading: boolean;
  error: unknown;
  /** The archive explains its own period; a preview has nothing to explain. */
  hint?: boolean;
  /**
   * Only the archive offers the download. The landing screen shows the next
   * few weeks of the same calendar, which is a glance, not a document.
   */
  exportAs?: string;
}) {
  const { t } = useTranslation();

  /** "established" is shown as the host's name; the rest have a label. */
  const hospitality = useCallback(
    (status: string | null | undefined, host: string | null | undefined) => {
      if (!status) return null;
      if (status === "established") return host ?? null;
      if (status === "not_provided") return t("local_assignation.hospitality_not_provided");
      if (status === "not_available") return t("local_assignation.hospitality_not_available");
      // Same wrong-key mix-up as the calendar: this is a prompt to decide, not
      // the coordinator-facing "speaker already declined" notice.
      if (status === "speaker_no_accept") return t("local_assignation.hospitality_speaker_accept");
      return t("local_assignation.hospitality_not_established");
    },
    [t],
  );

  const week: ExportableColumn<AssignationRowBase> = useMemo(
    () => ({
      title: t("activerecord.attributes.local_assignation.week_begin"),
      dataIndex: "week_label",
      width: 190,
      fixed: "left",
      // The tag goes under the date instead of beside it: side by side the two
      // share the column's width and the date is what breaks.
      render: (value: string, row: AssignationRowBase) =>
        row.modified ? (
          <Space orientation="vertical" size={0} align="start">
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            <Tooltip title={t("icon.modified")}>
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                {t("icon.modified")}
              </Tag>
            </Tooltip>
          </Space>
        ) : (
          value
        ),
      exportValue: (row) =>
        row.modified ? `${row.week_label} (${t("icon.modified")})` : row.week_label,
    }),
    [t],
  );

  const event: ExportableColumn<AssignationRowBase> = useMemo(
    () => ({
      title: t("activerecord.attributes.local_assignation.event_type"),
      dataIndex: "event_type_label",
      width: 180,
      ellipsis: true,
    }),
    [t],
  );

  const notes: ExportableColumn<AssignationRowBase> = useMemo(
    () => ({
      title: t("activerecord.attributes.local_assignation.additional_notes"),
      dataIndex: "additional_notes",
      ellipsis: true,
    }),
    [t],
  );

  const columns = useMemo<ExportableColumn<AssignationRowBase>[]>(() => {
    if (scope === "circumscription") {
      return [week, event, notes];
    }

    const timing = [
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 120,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
    ];

    const speech = [
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right" as const,
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        width: 240,
        ellipsis: true,
      },
    ];

    if (scope === "congregation") {
      return [
        week,
        ...timing,
        event,
        {
          title: t("activerecord.attributes.local_assignation.circumscription_event"),
          dataIndex: "is_circumscription_event",
          width: 100,
          align: "center",
          render: (value: boolean) => (value ? <Tag color="blue">{t("yes")}</Tag> : null),
        },
        {
          title: t("activerecord.attributes.local_assignation.speaker_id"),
          dataIndex: "speaker_name",
          width: 180,
          ellipsis: true,
        },
        {
          title: t("activerecord.models.circumscription"),
          dataIndex: "speaker_circumscription",
          width: 90,
        },
        {
          title: t("activerecord.models.congregation"),
          dataIndex: "speaker_congregation",
          width: 170,
          ellipsis: true,
        },
        ...speech,
        {
          title: t("activerecord.attributes.local_assignation.speaker_host_name"),
          key: "hospitality",
          width: 160,
          ellipsis: true,
          render: (_value: unknown, row: AssignationRowBase) => {
            const item = row as CongregationArchiveRow;
            return hospitality(item.hospitality_status, item.hospitality_host_name);
          },
          // No dataIndex to fall back on: the cell is built from two fields.
          exportValue: (row: AssignationRowBase) => {
            const item = row as CongregationArchiveRow;
            return hospitality(item.hospitality_status, item.hospitality_host_name);
          },
        },
        notes,
      ];
    }

    return [
      week,
      ...timing,
      event,
      {
        title: t("activerecord.models.circumscription"),
        dataIndex: "circumscription",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "congregation",
        width: 170,
        ellipsis: true,
      },
      ...speech,
      {
        title: t("activerecord.attributes.local_assignation.speaker_host_name"),
        key: "hospitality",
        width: 160,
        ellipsis: true,
        render: (_value: unknown, row: AssignationRowBase) => {
          const item = row as SpeakerArchiveRow;
          return hospitality(item.hospitality_status, item.hospitality_host_name);
        },
        exportValue: (row: AssignationRowBase) => {
          const item = row as SpeakerArchiveRow;
          return hospitality(item.hospitality_status, item.hospitality_host_name);
        },
      },
      notes,
    ];
  }, [scope, t, week, event, notes, hospitality]);

  return (
    <>
      <DataTable<AssignationRowBase>
        columns={columns}
        rows={rows}
        exportAs={exportAs}
        loading={loading}
        error={error}
        rowKey="week_begin"
        // A week with nothing planned is a row too; greying it keeps the
        // calendar readable without dropping the gaps.
        rowClassName={(row) => (row.has_event ? "" : "archive-empty-week")}
      />

      {hint && (
        <Typography.Text type="secondary">
          {t("congregation.hints.hide_events_after_months")}
        </Typography.Text>
      )}

      <style>{`.archive-empty-week { opacity: 0.55; }`}</style>
    </>
  );
}

/** The three row types are what the columns above read; kept for the callers. */
export type { CongregationArchiveRow, SpeakerArchiveRow, ZoneArchiveRow };
